import { useEffect, useState, useRef, RefObject } from "react";
import { XYCoord } from "react-dnd";

const BOUND_THRESHOLD = 100;
const SCROLL_SPEED = 20; // basically dependant on hardware due to how fast/slow it can trigger refreshes

const getScrollDirectionX = ({
  position,
  containerLeftBound = Infinity,
  containerRightBound = -Infinity,
  firstColumnWidth = 0,
}: {
  position?: number;
  containerLeftBound?: number;
  containerRightBound?: number;
  firstColumnWidth?: number;
}): "left" | "right" | "stable" => {
  if (position === undefined) return "stable";
  if (position < containerLeftBound + BOUND_THRESHOLD + firstColumnWidth)
    return "left";
  if (position > containerRightBound - BOUND_THRESHOLD) return "right";
  return "stable";
};
const getScrollDirectionY = ({
  position,
  containerTopBound = Infinity,
  containerBottomBound = -Infinity,
}: {
  position?: number;
  containerTopBound?: number;
  containerBottomBound?: number;
}): "top" | "bottom" | "stable" => {
  if (position === undefined) return "stable";
  if (position < containerTopBound + BOUND_THRESHOLD) return "top";
  if (position > containerBottomBound - BOUND_THRESHOLD) return "bottom";
  return "stable";
};

export const useAutoScroll = (
  orientation: "horizontal" | "vertical",
  refContainer: RefObject<HTMLElement | null> | null,
  firstColumnWidth?: number
) => {
  const [itemPosition, updateItemPosition] = useState<XYCoord | null>();

  const scrollTimer = useRef<NodeJS.Timeout | null>(null);

  const containerBounds = refContainer?.current?.getBoundingClientRect();
  const direction =
    orientation === "horizontal"
      ? getScrollDirectionX({
          position: itemPosition?.x,
          containerLeftBound: containerBounds?.left,
          containerRightBound: containerBounds?.right,
          firstColumnWidth,
        })
      : getScrollDirectionY({
          position: itemPosition?.y,
          containerTopBound: containerBounds?.top,
          containerBottomBound: containerBounds?.bottom,
        });

  useEffect(() => {
    if (direction !== "stable") {
      scrollTimer.current = setInterval(() => {
        refContainer?.current?.scrollBy({
          left:
            orientation === "horizontal"
              ? direction === "right"
                ? SCROLL_SPEED
                : -SCROLL_SPEED
              : 0,
          top:
            orientation === "vertical"
              ? direction === "bottom"
                ? SCROLL_SPEED
                : -SCROLL_SPEED
              : 0,
        });
      }, 1);
    }
    return () => {
      if (scrollTimer.current) {
        clearInterval(scrollTimer.current);
      }
    };
  }, [direction, refContainer]);

  // takes care of cancelling the scroll when the user drops/releases the element on top of the autoScroll area
  useEffect(() => {
    const cancelInterval = () => {
      if (scrollTimer.current) {
        clearInterval(scrollTimer.current);
      }
    };
    refContainer?.current?.addEventListener("mouseup", cancelInterval);

    return () => {
      refContainer?.current?.removeEventListener("mouseup", cancelInterval);
    };
  }, [refContainer?.current]);
  return updateItemPosition;
};
